import React, { useState, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { 
  ChevronRightIcon, 
  MicrophoneIcon, 
  BeakerIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface QuickAction {
  id: string;
  title: string;
  hotkey: string;
  icon: React.ComponentType<any>;
  color: string;
  lastUsed?: any;
}

interface QuickActionPanelProps {
  onAction: (actionId: string, data?: any) => void;
  isVisible: boolean;
  onToggle: () => void;
}

const QuickActionPanel: React.FC<QuickActionPanelProps> = ({ 
  onAction, 
  isVisible, 
  onToggle 
}) => {
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);

  const quickActions: QuickAction[] = [
    {
      id: 'feed',
      title: 'Покормить',
      hotkey: 'Alt+F',
      icon: BeakerIcon,
      color: 'bg-green-500',
      lastUsed: {
        stackId: 1,
        feedType: 'отруби',
        amount: 1.5
      }
    },
    {
      id: 'measure',
      title: 'Замерить',
      hotkey: 'Alt+M',
      icon: ClipboardDocumentIcon,
      color: 'bg-blue-500',
      lastUsed: {
        stackId: 1,
        temperature: 22,
        humidity: 75
      }
    },
    {
      id: 'harvest',
      title: 'Биогумус',
      hotkey: 'Alt+H',
      icon: ArrowPathIcon,
      color: 'bg-yellow-500',
      lastUsed: {
        stackId: 3,
        volume: 20
      }
    },
    {
      id: 'voice',
      title: 'Голос',
      hotkey: 'Alt+V',
      icon: MicrophoneIcon,
      color: 'bg-purple-500'
    },
    {
      id: 'mobile_mode',
      title: 'Мобильный',
      hotkey: 'Ctrl+M',
      icon: ChevronRightIcon,
      color: 'bg-orange-500'
    }
  ];

  // Регистрируем горячие клавиши
  useHotkeys('alt+f', () => handleActionClick('feed'), []);
  useHotkeys('alt+m', () => handleActionClick('measure'), []);
  useHotkeys('alt+h', () => handleActionClick('harvest'), []);
  useHotkeys('alt+v', () => handleActionClick('voice'), []);
  useHotkeys('ctrl+b', () => onAction('bulk_mode'), []);
  useHotkeys('ctrl+m', () => handleActionClick('mobile_mode'), []);
  useHotkeys('escape', () => setSelectedAction(null), []);

  const handleActionClick = (actionId: string) => {
    setSelectedAction(actionId);
    onAction(actionId);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed right-4 top-20 z-50 transition-all duration-300 ${
      isMinimized ? 'w-16' : 'w-80'
    }`}>
      {/* Заголовок панели */}
      <div className="bg-white rounded-t-lg shadow-lg border border-gray-200 p-3 flex justify-between items-center">
        <h3 className={`font-semibold text-gray-900 ${isMinimized ? 'hidden' : 'block'}`}>
          ⚡ Быстрые действия
        </h3>
        <div className="flex space-x-2">
          <button
            onClick={handleMinimize}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title={isMinimized ? 'Развернуть' : 'Свернуть'}
          >
            <ChevronRightIcon className={`w-4 h-4 transform transition-transform ${
              isMinimized ? 'rotate-0' : 'rotate-90'
            }`} />
          </button>
          <button
            onClick={onToggle}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Закрыть"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Содержимое панели */}
      <div className={`bg-white rounded-b-lg shadow-lg border-x border-b border-gray-200 ${
        isMinimized ? 'hidden' : 'block'
      }`}>
        
        {/* Кнопки быстрых действий */}
        <div className="p-4 space-y-3">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => handleActionClick(action.id)}
              className={`w-full p-3 rounded-lg border-2 transition-all duration-200 ${
                selectedAction === action.id
                  ? 'border-blue-500 bg-blue-50 transform scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${action.color} text-white`}>
                  <action.icon className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">{action.title}</span>
                    <kbd className="px-2 py-1 text-xs bg-gray-100 rounded text-gray-600">
                      {action.hotkey}
                    </kbd>
                  </div>
                  {action.lastUsed && (
                    <div className="text-xs text-gray-500 mt-1">
                      {action.id === 'feed' && `${action.lastUsed.feedType}, ${action.lastUsed.amount}кг`}
                      {action.id === 'measure' && `${action.lastUsed.temperature}°C, ${action.lastUsed.humidity}%`}
                      {action.id === 'harvest' && `${action.lastUsed.volume}л`}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Режим массового ввода */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={() => onAction('bulk_mode')}
            className="w-full p-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg hover:from-purple-600 hover:to-blue-600 transition-all duration-200 transform hover:scale-105"
          >
            <div className="flex items-center justify-center space-x-2">
              <ArrowPathIcon className="w-5 h-5" />
              <span className="font-medium">Массовый ввод</span>
              <kbd className="px-2 py-1 text-xs bg-black bg-opacity-20 rounded">
                Ctrl+B
              </kbd>
            </div>
          </button>
        </div>

        {/* Подсказки */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 rounded-b-lg">
          <div className="text-xs text-gray-600">
            <p className="font-medium mb-1">💡 Подсказки:</p>
            <ul className="space-y-1">
              <li>• <kbd className="px-1 bg-gray-200 rounded text-xs">Tab</kbd> - следующее поле</li>
              <li>• <kbd className="px-1 bg-gray-200 rounded text-xs">Enter</kbd> - сохранить</li>
              <li>• <kbd className="px-1 bg-gray-200 rounded text-xs">Esc</kbd> - отмена</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActionPanel;
